import * as THREE from "three";
import { cheapTextures, normalMapFrom, roughFrom } from "./mapTextures.js";

/**
 * Procedural, tileable AVATAR surfaces — the character equivalent of
 * `mapTextures`, added 2026-08-01 (mission `graphics`).
 *
 * Until now every avatar part was a FLAT, untextured `MeshToonMaterial`: one
 * solid colour banded into 6 cel steps. That is the single loudest "toy" signal
 * left on the character — a real body at 10 m reads as cloth over armour
 * because its surface has grain, seams and a varying specular response, and a
 * cel band has none of the three. The maps here are near-WHITE so the cosmetic
 * `bodyColor` still tints them exactly as before (same contract as
 * `mapTextures`), and each family ships albedo + a real tangent-space normal +
 * a roughness map so the light response varies across the body.
 *
 * The body itself has two materials:
 *  - `suit`  — technical fabric over armour plates: twill weave, quilted panel
 *              seams with stitch rows, chest/shoulder plate edges, scuffs.
 *  - `gear`  — the dark parts (boots, neck, helmet): pebbled rubber with
 *              moulded ribs, glossier than the cloth.
 *  - `glove` — the HANDS only (2026-08-01): sewn panels, padded palm zones, a
 *              knurled grip print, saddle stitch and knuckle creases. Split out
 *              of `gear` because a boot sole on a first-person hand reads as
 *              corrugated cardboard at 30 cm.
 *
 * The five below (added 2026-08-01, the COSMETIC GEAR pass) surface the worn
 * cosmetics — cowboy hat/vest/holsters, the pirate tricorn and eyepatch, the
 * hordebearer skull mask, the jetpack and the drop-in parachute. Every one of
 * those was still a flat `MeshToonMaterial`/untextured box on a body and a gun
 * that are both PBR since earlier today, which made the accessories the crudest
 * thing on the model:
 *  - `felt`    — hat felt: short fibrous nap, a pressed hatband, dust on the
 *                crown. Very matte, almost no specular.
 *  - `leather` — vest/belt/holster/eyepatch: creased hide, open pores, a tooled
 *                border with saddle stitching, rubbed edges.
 *  - `bone`    — skull mask and badge: mottled cortical bone, hairline cracks,
 *                cranial sutures, porous foramina.
 *  - `nylon`   — parachute canopy: real RIPSTOP (the reinforcing thread grid on
 *                a coarser pitch than the weave) plus taped seams.
 *  - `plate`   — jetpack shell: painted alloy panels, panel gaps, dome rivets
 *                and paint chipped back to bare metal.
 *
 * Budget: albedo 256², normal 256², roughness 128², generated ONCE for the tab
 * and shared by every avatar in the room (one GPU upload per map, not per
 * player). `userData.shared` keeps `disposeDeep` off them — avatars are torn
 * down constantly (join/leave, the cosmetics preview rebuilds on every click).
 * Normal + roughness are skipped on lowSpec machines, same rule as the map
 * families.
 *
 * Tiling: avatar parts keep their RoundedBox per-face 0..1 UVs, so the tile
 * density is baked into the UVs per part (`avatar.ts` scales them by the part's
 * size / TILE_M) rather than into `texture.repeat` — one shared texture cannot
 * carry a different repeat per limb.
 */

const ALBEDO = 256;
const ROUGH = 128;
/** World metres per texture tile. A shoulder is ~0.2 m, a torso ~0.5 m: at
 *  0.42 m the weave is fine enough to read as cloth and coarse enough to
 *  survive the mip chain at gameplay distance. */
export const TILE_M = 0.42;

export type AvatarSurface = "suit" | "gear" | "glove" | "felt" | "leather" | "bone" | "nylon" | "plate";

interface Layers {
  albedo: HTMLCanvasElement;
  height: HTMLCanvasElement;
  bump: number;
  roughLo: number;
  roughHi: number;
}

interface FamilyTex {
  map: THREE.CanvasTexture;
  normal: THREE.CanvasTexture | null;
  roughness: THREE.CanvasTexture | null;
}

let cache: Partial<Record<AvatarSurface, FamilyTex>> | null = null;

function cvs(size: number): { c: HTMLCanvasElement; g: CanvasRenderingContext2D } {
  const c = document.createElement("canvas");
  c.width = c.height = size;
  return { c, g: c.getContext("2d", { willReadFrequently: true })! };
}

/** Per-pixel grain straight into the ImageData (same reason as mapTextures:
 *  a fillRect per sample costs hundreds of ms of canvas state changes). */
function grain(g: CanvasRenderingContext2D, n: number, alpha: number, size: number): void {
  const img = g.getImageData(0, 0, size, size);
  const d = img.data;
  for (let i = 0; i < n; i++) {
    const j = (((Math.random() * size) | 0) + ((Math.random() * size) | 0) * size) * 4;
    const v = 140 + Math.random() * 115;
    d[j] += (v - d[j]) * alpha;
    d[j + 1] += (v - d[j + 1]) * alpha;
    d[j + 2] += (v - d[j + 2]) * alpha;
  }
  g.putImageData(img, 0, 0);
}

/** Technical fabric over armour plate. Everything is drawn on a 256 grid with
 *  wrapping strokes so the result tiles in both axes. */
function makeSuit(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#ebebeb"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);

  // 1) TWILL WEAVE — diagonal thread pairs, 4 px pitch. This is the base
  //    micro-detail: without it a suit is a painted surface at any distance.
  for (let i = -ALBEDO; i < ALBEDO * 2; i += 4) {
    for (const [ctx, a, b] of [[g, "rgba(255,255,255,0.16)", "rgba(70,72,80,0.16)"] as const,
      [hg, "rgba(255,255,255,0.20)", "rgba(0,0,0,0.20)"] as const]) {
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = a;
      ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i + ALBEDO, ALBEDO); ctx.stroke();
      ctx.strokeStyle = b;
      ctx.beginPath(); ctx.moveTo(i + 2, 0); ctx.lineTo(i + 2 + ALBEDO, ALBEDO); ctx.stroke();
    }
  }
  grain(g, 24000, 0.10, ALBEDO);

  // 2) ARMOUR PLATES — two horizontal bands per tile, raised, with a bevel.
  //    These are what make the silhouette read as segmented gear rather than a
  //    single stretched sock.
  const plates: [number, number][] = [[18, 74], [140, 62]];
  for (const [y, h] of plates) {
    g.fillStyle = "rgba(255,255,255,0.12)"; g.fillRect(0, y, ALBEDO, h);
    hg.fillStyle = "rgba(190,190,190,1)"; hg.fillRect(0, y, ALBEDO, h);
    // bevelled top edge (lit) and a shadowed bottom edge
    g.fillStyle = "rgba(255,255,255,0.26)"; g.fillRect(0, y, ALBEDO, 3);
    g.fillStyle = "rgba(40,42,50,0.30)"; g.fillRect(0, y + h - 3, ALBEDO, 3);
    hg.fillStyle = "rgba(255,255,255,0.9)"; hg.fillRect(0, y, ALBEDO, 2);
    hg.fillStyle = "rgba(0,0,0,0.9)"; hg.fillRect(0, y + h - 2, ALBEDO, 2);
    // plate split lines (vertical grooves, wrapping)
    for (const x of [40, 128, 210]) {
      g.strokeStyle = "rgba(30,32,40,0.45)"; g.lineWidth = 2;
      g.beginPath(); g.moveTo(x, y); g.lineTo(x, y + h); g.stroke();
      hg.strokeStyle = "rgba(0,0,0,0.85)"; hg.lineWidth = 3;
      hg.beginPath(); hg.moveTo(x, y); hg.lineTo(x, y + h); hg.stroke();
    }
  }

  // 3) STITCH ROWS along the cloth gaps between plates — dashes, not lines, so
  //    they read as thread at close range and vanish cleanly into the mips.
  for (const y of [104, 122, 226, 244]) {
    for (let x = 3; x < ALBEDO; x += 9) {
      g.fillStyle = "rgba(45,48,56,0.40)"; g.fillRect(x, y, 5, 1.6);
      hg.fillStyle = "rgba(50,50,50,0.8)"; hg.fillRect(x, y, 5, 2);
    }
  }

  // 4) WEAR — scuffed patches where a body actually rubs. Albedo only: a scuff
  //    changes colour and gloss, not shape.
  for (let i = 0; i < 26; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 6 + Math.random() * 26;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, "rgba(120,118,112,0.16)");
    grd.addColorStop(1, "rgba(120,118,112,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  return { albedo: c, height: hc, bump: 1.9, roughLo: 0.42, roughHi: 0.96 };
}

/** Moulded rubber / leather: gloves, boots, neck seal, helmet shell. */
function makeGear(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#e4e4e4"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#7a7a7a"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  grain(g, 30000, 0.16, ALBEDO);
  // pebbled grain — a dense field of small domes (leather/rubber's signature)
  for (let i = 0; i < 900; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1.4 + Math.random() * 3.4;
    g.fillStyle = "rgba(255,255,255,0.16)";
    g.beginPath(); g.ellipse(x - r * 0.2, y - r * 0.25, r * 0.7, r * 0.6, 0, 0, 7); g.fill();
    g.fillStyle = "rgba(30,30,36,0.18)";
    g.beginPath(); g.ellipse(x + r * 0.3, y + r * 0.35, r * 0.55, r * 0.45, 0, 0, 7); g.fill();
    const dome = hg.createRadialGradient(x, y, 0, x, y, r);
    dome.addColorStop(0, "rgba(255,255,255,0.75)");
    dome.addColorStop(1, "rgba(122,122,122,0)");
    hg.fillStyle = dome; hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
  }
  // moulded ribs — deep grooves every 32 px, the grip pattern on a glove/sole
  for (let y = 0; y < ALBEDO; y += 32) {
    g.fillStyle = "rgba(20,22,28,0.34)"; g.fillRect(0, y, ALBEDO, 5);
    g.fillStyle = "rgba(255,255,255,0.20)"; g.fillRect(0, y + 5, ALBEDO, 2);
    hg.fillStyle = "rgba(0,0,0,0.9)"; hg.fillRect(0, y, ALBEDO, 5);
    hg.fillStyle = "rgba(255,255,255,0.55)"; hg.fillRect(0, y + 5, ALBEDO, 2);
  }
  return { albedo: c, height: hc, bump: 2.4, roughLo: 0.28, roughHi: 0.8 };
}

/**
 * SHOOTING GLOVE (2026-08-01, mission `graphics` — the HANDS pass).
 *
 * The hands were wearing `gear`, which is a BOOT SOLE: pebbled rubber with a
 * moulded rib every 32 px running straight across. On a boot at 1.7 m that
 * reads fine; on the first-person hands — the two objects on screen for 100% of
 * the match, at 30 cm from the lens — the ribs read as corrugated cardboard and
 * every finger got the same stripe in the same place.
 *
 * A glove is not a moulded surface, it is a SEWN one, so the features here are
 * the ones a hand actually has:
 *  - PADDED zones (thenar/hypothenar/finger pads) — raised, with a soft edge,
 *    which is what makes a palm read as flesh under fabric rather than a slab.
 *  - a knurled DIAMOND grip patch (the rubber print on a shooting glove's palm)
 *  - saddle-stitched SEAMS between the panels, running BOTH ways so a curled
 *    finger and a flat back both cross one
 *  - knuckle CREASE arcs and fine skin-fold wrinkles
 * Glossier and darker-responding than `gear`: worn leather picks up a sheen on
 * the pads and stays matte in the creases, which is the one-material-two-
 * behaviours trick the paving/cloth families use.
 */
function makeGlove(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#e7e5e1"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#787878"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  grain(g, 26000, 0.13, ALBEDO);

  // 1) PADS — soft raised islands. Placed on a jittered lattice so the tile has
  //    no repeating rhythm; radial gradients on BOTH layers so the edge is a
  //    roll-off, not a cut.
  for (const [px, py, rx, ry] of [
    [56, 60, 34, 26], [176, 48, 30, 22], [96, 168, 40, 28], [214, 150, 26, 20],
    [24, 210, 22, 18], [140, 232, 30, 18],
  ] as const) {
    const r = Math.max(rx, ry);
    const gd = hg.createRadialGradient(px, py, r * 0.25, px, py, r);
    gd.addColorStop(0, "rgba(215,215,215,1)");
    gd.addColorStop(1, "rgba(120,120,120,0)");
    hg.save(); hg.translate(px, py); hg.scale(rx / r, ry / r);
    hg.fillStyle = gd; hg.beginPath(); hg.arc(0, 0, r, 0, 7); hg.fill(); hg.restore();
    const ga = g.createRadialGradient(px, py, r * 0.2, px, py, r);
    ga.addColorStop(0, "rgba(255,255,255,0.22)");
    ga.addColorStop(1, "rgba(255,255,255,0)");
    g.save(); g.translate(px, py); g.scale(rx / r, ry / r);
    g.fillStyle = ga; g.beginPath(); g.arc(0, 0, r, 0, 7); g.fill(); g.restore();
  }

  // 2) KNURLED GRIP PATCH — a diamond lattice, the signature of a palm print.
  //    Drawn as two crossing stroke families clipped to one region of the tile,
  //    which is what keeps it from becoming a full-surface pattern (a glove is
  //    gripped on the palm and smooth on the back).
  g.save(); hg.save();
  for (const ctx of [g, hg]) { ctx.beginPath(); ctx.rect(104, 96, 108, 96); ctx.clip(); }
  for (let i = -128; i < 256; i += 9) {
    for (const [ctx, lit, dark] of [
      [g, "rgba(255,255,255,0.30)", "rgba(28,30,36,0.34)"] as const,
      [hg, "rgba(255,255,255,0.65)", "rgba(0,0,0,0.55)"] as const]) {
      ctx.lineWidth = 2.2;
      ctx.strokeStyle = lit;
      ctx.beginPath(); ctx.moveTo(i, 96); ctx.lineTo(i + 96, 192); ctx.stroke();
      ctx.strokeStyle = dark;
      ctx.beginPath(); ctx.moveTo(i, 192); ctx.lineTo(i + 96, 96); ctx.stroke();
    }
  }
  g.restore(); hg.restore();

  // 3) PANEL SEAMS + saddle stitch, both axes. The stitch is a run of short
  //    slanted dashes (a lockstitch), raised in height and light in albedo.
  const seam = (x0: number, y0: number, x1: number, y1: number): void => {
    for (const [ctx, col, w] of [
      [g, "rgba(38,40,48,0.34)", 2.4] as const,
      [hg, "rgba(0,0,0,0.7)", 3.0] as const]) {
      ctx.strokeStyle = col; ctx.lineWidth = w;
      ctx.beginPath(); ctx.moveTo(x0, y0); ctx.lineTo(x1, y1); ctx.stroke();
    }
    const len = Math.hypot(x1 - x0, y1 - y0), ux = (x1 - x0) / len, uy = (y1 - y0) / len;
    for (let s = 3; s < len; s += 9) {
      const sx = x0 + ux * s, sy = y0 + uy * s;
      for (const [ctx, col] of [[g, "rgba(246,242,232,0.8)"] as const, [hg, "rgba(255,255,255,0.8)"] as const]) {
        ctx.save(); ctx.translate(sx, sy); ctx.rotate(Math.atan2(uy, ux) + 0.55);
        ctx.fillStyle = col; ctx.fillRect(-2.6, -1.1, 5.2, 2.2); ctx.restore();
      }
    }
  };
  seam(0, 88, ALBEDO, 88);
  seam(0, 200, ALBEDO, 200);
  seam(88, 0, 88, ALBEDO);
  seam(232, 0, 232, ALBEDO);

  // 4) KNUCKLE CREASES — arcs cut into the height field, plus fine wrinkles.
  //    Creases are the difference between "a hand" and "a mitten".
  for (const [cx, cy, r, a0] of [[56, 34, 26, 0.2], [176, 118, 22, 2.6], [100, 224, 30, 3.4]] as const) {
    for (let k = 0; k < 3; k++) {
      hg.strokeStyle = "rgba(0,0,0,0.5)"; hg.lineWidth = 2.4 - k * 0.5;
      hg.beginPath(); hg.arc(cx, cy, r + k * 5, a0, a0 + 1.5); hg.stroke();
      g.strokeStyle = "rgba(46,44,44,0.16)"; g.lineWidth = 2 - k * 0.4;
      g.beginPath(); g.arc(cx, cy, r + k * 5, a0, a0 + 1.5); g.stroke();
    }
  }
  for (let i = 0; i < 220; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const a = Math.random() * Math.PI, len = 3 + Math.random() * 9;
    hg.strokeStyle = "rgba(0,0,0,0.22)"; hg.lineWidth = 1;
    hg.beginPath(); hg.moveTo(x, y); hg.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len); hg.stroke();
  }
  // 5) rubbed sheen on the high points (albedo only — polish, not shape)
  for (let i = 0; i < 18; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 8 + Math.random() * 22;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, "rgba(255,250,240,0.18)");
    grd.addColorStop(1, "rgba(255,250,240,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  return { albedo: c, height: hc, bump: 2.0, roughLo: 0.30, roughHi: 0.92 };
}

/** Short fibrous nap. A felt hat has almost no specular and no hard edges —
 *  the read is entirely in the fibre direction scatter, so the height field is
 *  many thousands of 1–3 px hairs rather than any drawn feature. */
function makeFelt(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#e6e3de"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#808080"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  grain(g, 34000, 0.12, ALBEDO);
  // fibre nap — short strokes at a biased random angle (felt is pressed, so the
  // fibres share a rough direction instead of being isotropic)
  for (let i = 0; i < 4200; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    const a = 0.5 + (Math.random() - 0.5) * 1.6, len = 1.5 + Math.random() * 3.5;
    const dx = Math.cos(a) * len, dy = Math.sin(a) * len;
    const lit = Math.random() < 0.5;
    g.strokeStyle = lit ? "rgba(255,255,255,0.13)" : "rgba(58,54,48,0.13)";
    g.lineWidth = 1;
    g.beginPath(); g.moveTo(x, y); g.lineTo(x + dx, y + dy); g.stroke();
    hg.strokeStyle = lit ? "rgba(255,255,255,0.16)" : "rgba(0,0,0,0.16)";
    hg.lineWidth = 1;
    hg.beginPath(); hg.moveTo(x, y); hg.lineTo(x + dx, y + dy); hg.stroke();
  }
  // pressed hatband crease — one wrapping horizontal band per tile
  g.fillStyle = "rgba(70,64,56,0.16)"; g.fillRect(0, 150, ALBEDO, 22);
  hg.fillStyle = "rgba(60,60,60,0.7)"; hg.fillRect(0, 150, ALBEDO, 22);
  hg.fillStyle = "rgba(255,255,255,0.5)"; hg.fillRect(0, 146, ALBEDO, 4);
  // trail dust settled on the felt (albedo only — dust has no relief)
  for (let i = 0; i < 22; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 10 + Math.random() * 34;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, "rgba(206,190,160,0.20)");
    grd.addColorStop(1, "rgba(206,190,160,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  return { albedo: c, height: hc, bump: 1.1, roughLo: 0.78, roughHi: 1.0 };
}

/** Worn hide: creases, pores, a tooled border with saddle stitch. */
function makeLeather(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#e8e4de"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#7e7e7e"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  grain(g, 26000, 0.13, ALBEDO);
  // CREASE NETWORK — the signature of used leather. Wandering polylines that
  // wrap, drawn dark in albedo and cut into the height field.
  for (let i = 0; i < 130; i++) {
    let x = Math.random() * ALBEDO, y = Math.random() * ALBEDO;
    let a = Math.random() * Math.PI * 2;
    g.strokeStyle = "rgba(52,44,36,0.22)"; g.lineWidth = 0.9 + Math.random();
    hg.strokeStyle = "rgba(0,0,0,0.35)"; hg.lineWidth = 1.6;
    g.beginPath(); g.moveTo(x, y); hg.beginPath(); hg.moveTo(x, y);
    const segs = 3 + ((Math.random() * 5) | 0);
    for (let s = 0; s < segs; s++) {
      a += (Math.random() - 0.5) * 1.1;
      x += Math.cos(a) * 9; y += Math.sin(a) * 9;
      g.lineTo(x, y); hg.lineTo(x, y);
    }
    g.stroke(); hg.stroke();
  }
  // pores
  for (let i = 0; i < 2600; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.5 + Math.random() * 1.1;
    hg.fillStyle = "rgba(0,0,0,0.30)"; hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
    g.fillStyle = "rgba(48,40,32,0.14)"; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
  }
  // TOOLED BORDER + saddle stitch: two wrapping grooves with slanted stitches
  // between them. This is the feature that says "made", not "moulded".
  for (const y of [26, 214]) {
    g.strokeStyle = "rgba(40,32,26,0.35)"; g.lineWidth = 2;
    g.beginPath(); g.moveTo(0, y); g.lineTo(ALBEDO, y); g.stroke();
    hg.strokeStyle = "rgba(0,0,0,0.8)"; hg.lineWidth = 3;
    hg.beginPath(); hg.moveTo(0, y); hg.lineTo(ALBEDO, y); hg.stroke();
    for (let x = 2; x < ALBEDO; x += 11) {
      g.save(); g.translate(x, y + 9); g.rotate(0.5);
      g.fillStyle = "rgba(236,226,206,0.85)"; g.fillRect(-3, -1.2, 6, 2.4);
      g.restore();
      hg.save(); hg.translate(x, y + 9); hg.rotate(0.5);
      hg.fillStyle = "rgba(255,255,255,0.75)"; hg.fillRect(-3, -1.2, 6, 2.4);
      hg.restore();
    }
  }
  // rubbed-through highlights along the wear line
  for (let i = 0; i < 30; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 5 + Math.random() * 20;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    grd.addColorStop(0, "rgba(255,246,228,0.22)");
    grd.addColorStop(1, "rgba(255,246,228,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  return { albedo: c, height: hc, bump: 1.7, roughLo: 0.38, roughHi: 0.88 };
}

/** Cortical bone: mottled, cracked, sutured, porous. */
function makeBone(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#efece1"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#8a8a8a"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  // mottling — old bone is never one tone
  for (let i = 0; i < 90; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 8 + Math.random() * 40;
    const grd = g.createRadialGradient(x, y, 0, x, y, r);
    const dark = Math.random() < 0.6;
    grd.addColorStop(0, dark ? "rgba(150,138,112,0.20)" : "rgba(255,253,244,0.24)");
    grd.addColorStop(1, "rgba(150,138,112,0)");
    g.fillStyle = grd; g.fillRect(x - r, y - r, r * 2, r * 2);
  }
  grain(g, 20000, 0.09, ALBEDO);
  // porous foramina — small pits, denser than leather pores and deeper
  for (let i = 0; i < 1500; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 0.6 + Math.random() * 1.6;
    hg.fillStyle = "rgba(0,0,0,0.45)"; hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
    g.fillStyle = "rgba(120,110,90,0.22)"; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
  }
  // hairline cracks + a wandering cranial SUTURE (the zig-zag seam) per tile
  const seam = (jag: number, amp: number, dark: string, hdark: string, lw: number): void => {
    let x = 0, y = 40 + Math.random() * 176;
    g.strokeStyle = dark; g.lineWidth = lw;
    hg.strokeStyle = hdark; hg.lineWidth = lw + 0.8;
    g.beginPath(); g.moveTo(0, y); hg.beginPath(); hg.moveTo(0, y);
    while (x < ALBEDO) {
      x += jag; y += (Math.random() - 0.5) * amp;
      g.lineTo(x, y); hg.lineTo(x, y);
    }
    g.stroke(); hg.stroke();
  };
  seam(5, 11, "rgba(96,86,66,0.55)", "rgba(0,0,0,0.85)", 2.2);   // suture
  for (let i = 0; i < 6; i++) seam(16, 5, "rgba(88,78,60,0.30)", "rgba(0,0,0,0.5)", 1.1); // cracks
  return { albedo: c, height: hc, bump: 1.5, roughLo: 0.5, roughHi: 0.92 };
}

/** Parachute ripstop: a fine weave with a coarser reinforcing thread grid on
 *  top (the visual signature of canopy fabric) plus taped seams. */
function makeNylon(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#f0eeea"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#828282"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  // base weave, 3 px pitch, both axes
  for (let i = 0; i < ALBEDO; i += 3) {
    g.fillStyle = "rgba(60,62,70,0.10)"; g.fillRect(i, 0, 1.4, ALBEDO); g.fillRect(0, i, ALBEDO, 1.4);
    hg.fillStyle = "rgba(0,0,0,0.14)"; hg.fillRect(i, 0, 1.4, ALBEDO); hg.fillRect(0, i, ALBEDO, 1.4);
  }
  // RIPSTOP grid — heavier thread every 24 px, raised
  for (let i = 0; i < ALBEDO; i += 24) {
    g.fillStyle = "rgba(255,255,255,0.30)"; g.fillRect(i, 0, 2.6, ALBEDO); g.fillRect(0, i, ALBEDO, 2.6);
    g.fillStyle = "rgba(52,54,62,0.18)"; g.fillRect(i + 2.6, 0, 1.2, ALBEDO); g.fillRect(0, i + 2.6, ALBEDO, 1.2);
    hg.fillStyle = "rgba(255,255,255,0.85)"; hg.fillRect(i, 0, 2.6, ALBEDO); hg.fillRect(0, i, ALBEDO, 2.6);
  }
  // taped seam down one axis of the tile — canopies are sewn from gores
  g.fillStyle = "rgba(255,255,255,0.22)"; g.fillRect(120, 0, 14, ALBEDO);
  g.fillStyle = "rgba(60,62,70,0.22)"; g.fillRect(120, 0, 2, ALBEDO); g.fillRect(132, 0, 2, ALBEDO);
  hg.fillStyle = "rgba(210,210,210,1)"; hg.fillRect(120, 0, 14, ALBEDO);
  hg.fillStyle = "rgba(0,0,0,0.8)"; hg.fillRect(119, 0, 2, ALBEDO); hg.fillRect(133, 0, 2, ALBEDO);
  for (let y = 4; y < ALBEDO; y += 8) {
    g.fillStyle = "rgba(50,52,60,0.5)"; g.fillRect(122, y, 4, 2);
    g.fillStyle = "rgba(50,52,60,0.5)"; g.fillRect(128, y, 4, 2);
  }
  grain(g, 12000, 0.06, ALBEDO);
  return { albedo: c, height: hc, bump: 1.3, roughLo: 0.30, roughHi: 0.72 };
}

/** Painted alloy shell: panel gaps, dome rivets, chipped paint over bare metal.
 *  Same idea as the map `metal` family but at accessory scale (a jetpack tank
 *  is ~0.4 m, so the rivet pitch has to be much tighter than a hull's). */
function makePlate(): Layers {
  const { c, g } = cvs(ALBEDO);
  const { c: hc, g: hg } = cvs(ALBEDO);
  g.fillStyle = "#eceaea"; g.fillRect(0, 0, ALBEDO, ALBEDO);
  hg.fillStyle = "#8c8c8c"; hg.fillRect(0, 0, ALBEDO, ALBEDO);
  // orange-peel of sprayed paint
  grain(g, 22000, 0.07, ALBEDO);
  // PANEL GAPS — one cross per tile, cut deep
  for (const [x, y, w, h] of [[0, 96, ALBEDO, 3], [148, 0, 3, ALBEDO]] as const) {
    g.fillStyle = "rgba(36,38,46,0.55)"; g.fillRect(x, y, w, h);
    hg.fillStyle = "rgba(0,0,0,0.95)"; hg.fillRect(x, y, w, h);
    g.fillStyle = "rgba(255,255,255,0.22)"; g.fillRect(x, y - 2, w === ALBEDO ? w : 2, h === 3 ? 2 : h);
  }
  // DOME RIVETS along the panel edges
  const rivet = (x: number, y: number): void => {
    const r = 3.2;
    const d = g.createRadialGradient(x - 0.8, y - 0.9, 0, x, y, r);
    d.addColorStop(0, "rgba(255,255,255,0.55)");
    d.addColorStop(0.65, "rgba(214,212,212,0.25)");
    d.addColorStop(1, "rgba(48,50,58,0.45)");
    g.fillStyle = d; g.beginPath(); g.arc(x, y, r, 0, 7); g.fill();
    const hd = hg.createRadialGradient(x, y, 0, x, y, r);
    hd.addColorStop(0, "rgba(255,255,255,0.95)");
    hd.addColorStop(1, "rgba(140,140,140,0)");
    hg.fillStyle = hd; hg.beginPath(); hg.arc(x, y, r, 0, 7); hg.fill();
  };
  for (let x = 8; x < ALBEDO; x += 20) { rivet(x, 88); rivet(x, 108); }
  for (let y = 8; y < ALBEDO; y += 20) { rivet(140, y); rivet(160, y); }
  // CHIPS — paint knocked off to bright bare alloy, with a dark lip
  for (let i = 0; i < 60; i++) {
    const x = Math.random() * ALBEDO, y = Math.random() * ALBEDO, r = 1.5 + Math.random() * 5;
    g.fillStyle = "rgba(30,32,38,0.30)";
    g.beginPath(); g.ellipse(x, y, r * 1.25, r * 1.05, Math.random() * 3, 0, 7); g.fill();
    g.fillStyle = "rgba(255,255,255,0.5)";
    g.beginPath(); g.ellipse(x, y, r, r * 0.85, Math.random() * 3, 0, 7); g.fill();
    hg.fillStyle = "rgba(90,90,90,0.7)";
    hg.beginPath(); hg.ellipse(x, y, r, r * 0.85, 0, 0, 7); hg.fill();
  }
  return { albedo: c, height: hc, bump: 2.2, roughLo: 0.22, roughHi: 0.78 };
}

const MAKERS: Record<AvatarSurface, () => Layers> = {
  suit: makeSuit, gear: makeGear, glove: makeGlove,
  felt: makeFelt, leather: makeLeather, bone: makeBone,
  nylon: makeNylon, plate: makePlate,
};

function build(kind: AvatarSurface): FamilyTex {
  const cheap = cheapTextures();
  const mk = (canvas: HTMLCanvasElement, srgb: boolean): THREE.CanvasTexture => {
    const t = new THREE.CanvasTexture(canvas);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.anisotropy = 4;
    t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    t.userData.shared = true; // avatars are disposed constantly; these are not
    return t;
  };
  const l = MAKERS[kind]();
  return {
    map: mk(l.albedo, true),
    normal: cheap ? null : mk(normalMapFrom(l.height, l.bump, ALBEDO), false),
    roughness: cheap ? null : mk(roughFrom(l.albedo, l.roughLo, l.roughHi, ROUGH), false),
  };
}

/** Families are memoised INDIVIDUALLY rather than built as one block. Two of
 *  the seven (`suit`, `gear`) are on every avatar, but the five cosmetic ones
 *  are worn only on specific maps/pickups, and generating all of them was ~3x
 *  the cost of the pair — paid at the first `new Avatar()`, which happens
 *  mid-match when a player joins. Per-family means an unworn cosmetic costs
 *  nothing at all, and `warmAvatarTextures()` (called from the renderer's
 *  map-load precompile, i.e. under the load screen) pays for the rest up front
 *  so nothing is ever generated during a fight. */
function familyTex(kind: AvatarSurface): FamilyTex {
  if (!cache) cache = {};
  let f = cache[kind];
  if (!f) {
    const t0 = performance.now();
    f = cache[kind] = build(kind);
    console.log(`[avatex] generated ${kind} in ${(performance.now() - t0).toFixed(0)} ms`);
  }
  return f;
}

/** Generate every family now (load screen), so none compiles mid-match. */
export function warmAvatarTextures(): void {
  const t0 = performance.now();
  let n = 0;
  for (const k of Object.keys(MAKERS) as AvatarSurface[]) {
    if (!cache?.[k]) { familyTex(k); n++; }
  }
  if (n) console.log(`[avatex] warmed ${n} avatar families in ${(performance.now() - t0).toFixed(0)} ms`);
}

/** Fold a family's albedo/normal/roughness into MeshStandardMaterial params —
 *  the avatar-side twin of `surfaceMaps`, so no call site can wire the albedo
 *  and forget the detail maps. `normalScale` is a shared per-family Vector2
 *  (never allocated per material). */
const normalScales = new Map<AvatarSurface, THREE.Vector2>();
/** Per-family relief strength. Felt is a nap, not a machined surface: at the
 *  body's 0.85 it turned into visual noise on a hat brim. Bone and plate carry
 *  the most, they are the two hard-shelled families. */
const NORMAL_STRENGTH: Record<AvatarSurface, number> = {
  suit: 0.85, gear: 0.85, glove: 0.95, felt: 0.45, leather: 0.8, bone: 0.9, nylon: 0.55, plate: 1.0,
};
export function avatarMaps(
  kind: AvatarSurface,
  params: THREE.MeshStandardMaterialParameters,
): THREE.MeshStandardMaterialParameters {
  const f = familyTex(kind);
  params.map = f.map;
  if (f.normal) {
    params.normalMap = f.normal;
    let s = normalScales.get(kind);
    if (!s) { const k = NORMAL_STRENGTH[kind]; s = new THREE.Vector2(k, k); normalScales.set(kind, s); }
    params.normalScale = s;
  }
  if (f.roughness) params.roughnessMap = f.roughness;
  return params;
}

// ---------------------------------------------------------------------------
// PHOTO DETAIL (2026-11-03). Owner: avatars may get "more realistic" but must
// "look the same" (CLAUDE.md invariant 5, amended). So the procedural LAYOUT
// above stays the authority (plates, seams, stitch rows, the near-white tint
// contract), and a real CC0 Poly Haven scan is folded in as MICRO-detail only:
//  - albedo  × (det/128): det.jpg is the scan's luminance high-passed offline
//             (mean exactly 128), so the tint/brightness of every cosmetic is
//             unchanged and only weave / grain / pores appear;
//  - normal  whiteout-blended with the scan's normal (layout relief kept);
//  - rough   × (rough/128): ratio map, so the family's gloss range is kept.
// Files: public/textures/avatar/<family>/{det,nor,rough}.jpg (512/512/256,
// pre-tiled to one TILE_M; credits + recipe in that folder's README).
// Load-gated like the map photos: fetched with the match (GameRenderer
// constructor), composited and re-uploaded under the load screen by
// renderer.precompile -> upgradeAvatarTextures(). The SAME texture objects are
// reused (image swap), so every material already holding them updates with no
// rewiring. Families without a scan (bone/nylon/plate) stay procedural; cheap
// machines never start the load.
// ---------------------------------------------------------------------------

const PHOTO_FAMILIES = ["suit", "gear", "glove", "leather", "felt"] as const;
type PhotoFamily = (typeof PHOTO_FAMILIES)[number];
const PHOTO_SIZE = 512;
/** How hard the scan's micro-detail lands on each family (albedo, normal). */
const PHOTO_MIX: Record<PhotoFamily, { alb: number; nor: number }> = {
  suit: { alb: 0.9, nor: 1.0 }, gear: { alb: 0.8, nor: 0.9 }, glove: { alb: 0.8, nor: 0.9 },
  leather: { alb: 0.9, nor: 1.0 }, felt: { alb: 1.0, nor: 0.7 },
};
interface PhotoSet { det: ImageData; nor: ImageData; rough: ImageData }
const photos = new Map<PhotoFamily, PhotoSet>();
const upgraded = new Set<PhotoFamily>();
let photoLoad: Promise<void> | null = null;

function pixels(img: CanvasImageSource, size: number): ImageData {
  const { g } = cvs(size);
  g.drawImage(img, 0, 0, size, size);
  return g.getImageData(0, 0, size, size);
}

/** Start fetching the avatar scans. Idempotent; `enabled=false` = no-op. */
export function startAvatarPhotoLoad(enabled: boolean): Promise<void> {
  if (photoLoad) return photoLoad;
  if (!enabled || cheapTextures()) { photoLoad = Promise.resolve(); return photoLoad; }
  const load = (url: string): Promise<HTMLImageElement> => {
    const img = new Image();
    img.decoding = "async";
    img.src = url;
    return img.decode().then(() => img);
  };
  photoLoad = Promise.all(PHOTO_FAMILIES.map(async (f) => {
    try {
      const [d, n, r] = await Promise.all(["det", "nor", "rough"].map((k) => load(`/textures/avatar/${f}/${k}.jpg`)));
      photos.set(f, { det: pixels(d, PHOTO_SIZE), nor: pixels(n, PHOTO_SIZE), rough: pixels(r, PHOTO_SIZE) });
    } catch { /* this family keeps the procedural surface */ }
  })).then(() => { console.log(`[avatex] ${photos.size}/${PHOTO_FAMILIES.length} photo detail sets loaded`); });
  return photoLoad;
}
export function avatarPhotoLoadPromise(): Promise<void> | null { return photoLoad; }

/** Fold the loaded scans into the shared family textures (load screen only).
 *  Returns the number of families upgraded this call. */
export function upgradeAvatarTextures(): number {
  let n = 0;
  for (const f of PHOTO_FAMILIES) {
    const p = photos.get(f);
    if (!p || upgraded.has(f)) continue;
    const tex = familyTex(f);
    if (!tex.normal || !tex.roughness) continue; // cheap path: never detailed
    const mix = PHOTO_MIX[f];
    const S = PHOTO_SIZE;
    // albedo
    {
      const { c, g } = cvs(S);
      g.imageSmoothingQuality = "high";
      g.drawImage(tex.map.image as HTMLCanvasElement, 0, 0, S, S);
      const img = g.getImageData(0, 0, S, S), d = img.data, det = p.det.data;
      for (let i = 0; i < d.length; i += 4) {
        const k = 1 + mix.alb * (det[i] / 128 - 1);
        d[i] = Math.min(255, d[i] * k); d[i + 1] = Math.min(255, d[i + 1] * k); d[i + 2] = Math.min(255, d[i + 2] * k);
      }
      g.putImageData(img, 0, 0);
      tex.map.image = c;
      tex.map.needsUpdate = true;
    }
    // normal: whiteout blend  n = normalize(n1.xy + s*n2.xy, n1.z * n2.z)
    {
      const { c, g } = cvs(S);
      g.drawImage(tex.normal.image as HTMLCanvasElement, 0, 0, S, S);
      const img = g.getImageData(0, 0, S, S), d = img.data, pn = p.nor.data;
      for (let i = 0; i < d.length; i += 4) {
        const ax = d[i] / 127.5 - 1, ay = d[i + 1] / 127.5 - 1, az = d[i + 2] / 127.5 - 1;
        const bx = (pn[i] / 127.5 - 1) * mix.nor, by = (pn[i + 1] / 127.5 - 1) * mix.nor, bz = pn[i + 2] / 127.5 - 1;
        const x = ax + bx, y = ay + by, z = az * bz;
        const l = Math.hypot(x, y, z) || 1;
        d[i] = (x / l + 1) * 127.5; d[i + 1] = (y / l + 1) * 127.5; d[i + 2] = (z / l + 1) * 127.5;
      }
      g.putImageData(img, 0, 0);
      tex.normal.image = c;
      tex.normal.needsUpdate = true;
    }
    // roughness ratio (stored at 512 in memory; the source file is 256)
    {
      const { c, g } = cvs(S);
      g.drawImage(tex.roughness.image as HTMLCanvasElement, 0, 0, S, S);
      const img = g.getImageData(0, 0, S, S), d = img.data, pr = p.rough.data;
      for (let i = 0; i < d.length; i += 4) {
        const v = Math.min(255, d[i] * (0.35 + 0.65 * pr[i] / 128));
        d[i] = d[i + 1] = d[i + 2] = v;
      }
      g.putImageData(img, 0, 0);
      tex.roughness.image = c;
      tex.roughness.needsUpdate = true;
    }
    upgraded.add(f);
    n++;
  }
  return n;
}

/** Shared family textures that exist right now (for the load-screen upload). */
export function avatarTextures(): THREE.Texture[] {
  const out: THREE.Texture[] = [];
  if (!cache) return out;
  for (const k of Object.keys(cache) as AvatarSurface[]) {
    const f = cache[k]!;
    out.push(f.map);
    if (f.normal) out.push(f.normal);
    if (f.roughness) out.push(f.roughness);
  }
  return out;
}
